import type { PostProcessorModule } from 'i18next';

/**
 * Тексты, которые показываются на ОДНОМ языке при любом языке интерфейса.
 *
 * 🔴 ЭТО НЕ ПОТЕРЯННЫЙ ПЕРЕВОД. Решение владельца 15.09.2026: все тексты блока
 * записи голоса — кнопки, подсказки, ошибки («The recording was cut off», «Listen
 * and decide», «Speak louder») — всегда по-английски, даже в русском и казахском
 * интерфейсе. Причина: приложение про английский, блок записи — практика речи, и
 * тексты вокруг неё тоже работают на обучение.
 *
 * ⚠️ ОДНО ПРАВИЛО НА ВСЁ ПРИЛОЖЕНИЕ, А НЕ ПРАВКА НА КАЖДОМ ВЫЗОВЕ. Эти ключи звучат в
 * уроке, в строке записи вверху, в рекордере домашки, а часть из них возвращают
 * `lib/stopReason.ts`, `lib/recordingIntegrity.ts` и `lib/openAnswerMin.ts`. Правка
 * по месту пропустила бы следующий вызов, и он молча заговорил бы по-русски.
 * Здесь решает сам ключ: какой бы код его ни спросил, текст один.
 *
 * ⚠️ РУССКИЕ И КАЗАХСКИЕ СТРОКИ В ЛОКАЛЯХ ОСТАЮТСЯ — их просто не показывают.
 * Передумали — убрать правило из списка ниже, и всё вернётся одной правкой.
 *
 * Сторож — `lib/fixedLanguageKeys.test.ts`.
 */

export type FixedLanguage = 'en' | 'ru' | 'kk';

export type FixedLanguageRule = {
  /** Полный ключ перевода, например `blockRenderers.recordStopMute`. */
  pattern: RegExp;
  lng: FixedLanguage;
};

export const FIXED_LANGUAGE_RULES: readonly FixedLanguageRule[] = [
  // Блок записи голоса: `blockRenderers.audio*` и `blockRenderers.record*`. Заглавная
  // после приставки — чтобы `blockRenderers.recording…` или `audioPlayer.*` не попали сюда случайно.
  { pattern: /^blockRenderers\.(audio|record)[A-Z]/, lng: 'en' },
  // Правило `diagnostic.*` (тест на уровень по-английски) снято 26.10.2026: страницу теста
  // отдаёт сама diagnostic-service, ключей сайта она не берёт.
];

/** Имя постпроцессора i18next — оно же стоит в `postProcess` при инициализации. */
export const FIXED_LANGUAGE_PROCESSOR = 'fixedLanguage';

/** На каком языке показывать ключ; `null` — как у интерфейса. */
export function fixedLanguageOf(
  key: string | readonly string[],
  rules: readonly FixedLanguageRule[] = FIXED_LANGUAGE_RULES,
): FixedLanguage | null {
  const keys = typeof key === 'string' ? [key] : key;
  for (const k of keys) {
    // Ключ может прийти с пространством имён: `translation:blockRenderers.audioStop`.
    const bare = k.includes(':') ? k.slice(k.indexOf(':') + 1) : k;
    for (const rule of rules) if (rule.pattern.test(bare)) return rule.lng;
  }
  return null;
}

type TranslatorLike = {
  language?: string;
  translate(key: string | readonly string[], options?: Record<string, unknown>): unknown;
};

/**
 * Постпроцессор i18next: у ключа из правил отдаёт текст на языке правила.
 *
 * ⚠️ ТЕКСТ БЕРЁТСЯ ЗАНОВО, А НЕ ПЕРЕВОДИТСЯ ИЗ ГОТОВОГО. Тот же ключ спрашивается с
 * `lng` правила и теми же параметрами — подстановки (`{{time}}`, `{{saved}}`) и правки
 * текстов с сервера для этого языка работают как обычно. `applyPostProcessor: false` —
 * штатный выключатель i18next, без него запрос ушёл бы в себя же.
 */
export const fixedLanguagePostProcessor: PostProcessorModule = {
  type: 'postProcessor',
  name: FIXED_LANGUAGE_PROCESSOR,
  process(value, key, options, translator) {
    const lng = fixedLanguageOf(key);
    if (!lng) return value;
    const t = translator as TranslatorLike;
    const opts = (options ?? {}) as Record<string, unknown>;
    const current = (typeof opts.lng === 'string' ? opts.lng : undefined) ?? t.language;
    if (current === lng) return value;
    const fixed = t.translate(key, { ...opts, lng, lngs: undefined, applyPostProcessor: false });
    return typeof fixed === 'string' && fixed ? fixed : value;
  },
};
